import { Button, H4, Intent, Spinner } from "@blueprintjs/core"
import { AxiosError } from "axios"
import { useState } from "react"

import { ApiKey, CreatedApiKey } from "../../../../api/apiKeys"
import { ConfirmDialog, ToastError } from "../../../components"
import { createToaster } from "../../../utils/createToaster"
import { ApiKeysContainer, HeaderRow, HeaderText } from "./ApiKeys.styled"
import { ApiKeysTable } from "./ApiKeysTable"
import { CreateApiKeyDialog } from "./CreateApiKeyDialog"
import {
  useApiKeys,
  useCreateApiKeyMutation,
  useRevokeApiKeyMutation,
} from "./hooks/useApiKeyQueries"

const errorMessage = (error: AxiosError, fallback: string) => {
  const data = error.response?.data as { error?: string | string[] } | undefined
  const detail = data?.error
  if (Array.isArray(detail)) return detail.join(" ")
  return detail ?? fallback
}

const ApiKeys = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [createdKey, setCreatedKey] = useState<CreatedApiKey | null>(null)
  const [keyToRevoke, setKeyToRevoke] = useState<ApiKey | null>(null)

  const { apiKeys, error, isLoading } = useApiKeys()

  const createMutation = useCreateApiKeyMutation({
    onSuccess: created => setCreatedKey(created),
    onError: error =>
      createToaster({
        message: errorMessage(error, "Unable to create token"),
        intent: Intent.DANGER,
      }),
  })

  const revokeMutation = useRevokeApiKeyMutation({
    onSuccess: () => {
      setKeyToRevoke(null)
      createToaster({ message: "Token revoked", intent: Intent.SUCCESS })
    },
    onError: error => {
      setKeyToRevoke(null)
      createToaster({
        message: errorMessage(error, "Unable to revoke token"),
        intent: Intent.DANGER,
      })
    },
  })

  const copyToken = (token: string) => {
    navigator.clipboard.writeText(token)
    createToaster({ message: "Token copied to clipboard", intent: Intent.SUCCESS })
  }

  return (
    <ApiKeysContainer flexDirection="column">
      <HeaderRow>
        <HeaderText>
          <H4>MCP tokens</H4>
          <p>
            Tokens let MCP clients (Claude Code, Claude Desktop, scripts) authenticate as you
            without a browser login. The plaintext token is shown exactly once at creation.
          </p>
        </HeaderText>
        {(apiKeys?.length ?? 0) > 0 && (
          <Button
            icon="add"
            intent={Intent.PRIMARY}
            text="New token"
            onClick={() => setIsCreateDialogOpen(true)}
          />
        )}
      </HeaderRow>
      {isLoading ? (
        <Spinner />
      ) : (
        <ApiKeysTable
          apiKeys={apiKeys ?? []}
          onRevoke={setKeyToRevoke}
          onCreate={() => setIsCreateDialogOpen(true)}
        />
      )}
      <CreateApiKeyDialog
        isOpen={isCreateDialogOpen}
        onClose={() => {
          setIsCreateDialogOpen(false)
          setCreatedKey(null)
        }}
        onCreate={payload => createMutation.mutate(payload)}
        isCreating={createMutation.isLoading}
        createdKey={createdKey}
        onCopyToken={copyToken}
      />
      <ConfirmDialog
        title="Revoke token?"
        dialogText={
          keyToRevoke
            ? `"${keyToRevoke.name}" will stop working immediately. This cannot be undone.`
            : ""
        }
        confirmButtonIntentColor={Intent.DANGER}
        confirmButtonText="Revoke"
        open={keyToRevoke !== null}
        close={() => setKeyToRevoke(null)}
        handleClick={() => keyToRevoke && revokeMutation.mutate(keyToRevoke.id)}
      />
      <ToastError error={error} errorMessage="Unable to load your tokens" />
    </ApiKeysContainer>
  )
}

export default ApiKeys
